import { FormControl, Grid, InputLabel, MenuItem, RadioGroup, Select, Stack, TextField, Typography } from "@mui/material";
import { AutoCompleteDataModel } from "components/MprcAutoComplete";
import { UnitAutoCompleteHierarchicalModel } from "components/MprcAutoCompleteHierarchical/AutoCompleteHierarchicalModel";
import { MprcDatePicker } from "components/MprcDatePicker";
import { MprcRadio } from "components/MprcRadio";
import { MprcRequestSnackbarErrorReport } from "components/MprcRequestErrorReport";
import { MprcSwitch } from "components/MprcSwitch";
import { GroupType, GroupUsages, TagUsage, UserOperationAction, UserOperationAdditionalInfoProps, UserOperationBlockProps, UserOperationGeneralInfoProps, UserOperationManagementProps, UserOperationRoleProps, UserOperationTagProps, UserOperationUnitProps, UserStatus } from "generated/CoreApi_v1";
import useDidUpdateEffect from "hooks/useNoInitialEffect";
import { useSession } from "providers/SessionProvider";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { CustomFieldInput } from "views/foundation/customField/components/CustomFieldInput";
import { GroupAutoComplete } from "views/foundation/group/components/GroupAutoComplete";
import { SecurityRoleAutoComplete } from "views/foundation/security/components/SecurityRoleAutoComplete";
import { TagAutoComplete } from "views/foundation/tag/components/TagAutoComplete";
import { TopicAutoComplete } from "views/foundation/topic/components/TopicAutoComplete";
import { UnitAutoCompleteHierarchical } from "views/foundation/unit/components/UnitAutoCompleteHierarchical";
import { UserAutoComplete } from "views/foundation/user/components/UserAutoComplete";
import { useUserBatchEditContext } from "views/foundation/user/Home/BatchEdit";
import { WorkplaceAutoComplete } from "views/foundation/workplace/components/WorkplaceAutoComplete";

const AddOrRemoveOperation = ({ value, onChange, enableReplaceOption = false, addText, removeText, replaceText }: { value: UserOperationAction, onChange: (action: UserOperationAction) => void; enableReplaceOption?: boolean, addText?: string, removeText?: string; replaceText?: string; }) => {

    const { t } = useTranslation();
    return (
        <RadioGroup row value={value} onChange={(e, value) => onChange(value as unknown as UserOperationAction)}>
            {enableReplaceOption && <MprcRadio label={replaceText ?? t("User.BatchEdit.SetOption")} value={UserOperationAction.Replace} />}
            <MprcRadio label={addText ?? t("User.BatchEdit.AddOption")} value={UserOperationAction.Add} />
            <MprcRadio label={removeText ?? t("User.BatchEdit.RemoveOption")} value={UserOperationAction.Remove} />
        </RadioGroup>
    );
};

export const UserTagsOperation: React.FC = () => {

    const { t } = useTranslation();
    const ctx = useUserBatchEditContext();

    const [props, setProps] = useState<UserOperationTagProps>({ action: UserOperationAction.Add } as UserOperationTagProps);
    const [tag, setTag] = useState<AutoCompleteDataModel>();

    const handleUpdateRecord = (name: keyof UserOperationTagProps, v: any) => {
        setProps(state => ({ ...state, [name]: v }));
    };

    const handleChangeTag = (item?: AutoCompleteDataModel) => {
        setTag(item);
        handleUpdateRecord("tagId", item?.key);
    };

    useEffect(() => {
        ctx.setOptions({ tag: props });
    }, [props]);

    return (
        <Stack spacing={1}>
            <AddOrRemoveOperation value={props.action} onChange={value => handleUpdateRecord("action", value)} />
            <TagAutoComplete
                label={t("User.BatchEdit.TagLabel")}
                usage={TagUsage.User}
                singleSelectMode="text"
                selectedItems={tag ? [tag] : []}
                onChange={items => handleChangeTag(items.length > 0 ? items[0] : undefined)}
            />
        </Stack>
    );
};

export const UserUnitsOperation: React.FC = () => {

    const { t } = useTranslation();
    const ctx = useUserBatchEditContext();

    const [props, setProps] = useState<UserOperationUnitProps>({ action: UserOperationAction.Replace } as UserOperationUnitProps);
    const [unit, setUnit] = useState<UnitAutoCompleteHierarchicalModel>();
    const [unitTypeId, setUnitTypeId] = useState<string>();

    var unitType = ctx.settings.unitTypes.find(p => p.unitTypeId == unitTypeId);

    const handleUpdateRecord = (name: keyof UserOperationUnitProps, v: any) => {
        setProps(state => ({ ...state, [name]: v }));
    };

    const handleChangeUnit = (item?: UnitAutoCompleteHierarchicalModel) => {
        setUnit(item);
        handleUpdateRecord("unitId", item?.key);
    };

    const handleChangeUnitType = (id: string) => {
        setUnitTypeId(id);
        handleChangeUnit(undefined);
    };

    useEffect(() => {
        ctx.setOptions({ unit: props });
    }, [props]);

    return (
        <Stack spacing={1}>
            <RadioGroup row value={props.action} onChange={(e, value) => handleUpdateRecord("action", value)}>
                <MprcRadio label={t("User.BatchEdit.DefineUnitOption")} value={UserOperationAction.Replace} />
                {unitType?.allowMultiSelection == true &&
                    <>
                        <MprcRadio label={t("User.BatchEdit.AddUnitOption")} value={UserOperationAction.Add} />
                        <MprcRadio label={t("User.BatchEdit.RemoveUnitOption")} value={UserOperationAction.Remove} />
                    </>
                }
            </RadioGroup>
            <Stack direction="row" spacing={2} alignItems="flex-end">
                <FormControl sx={{ width: 300 }}>
                    <InputLabel>{t("User.BatchEdit.UnitTypeLabel")}</InputLabel>
                    <Select
                        value={unitTypeId || ""}
                        label={t("User.BatchEdit.UnitTypeLabel")}
                        onChange={(e) => handleChangeUnitType(e.target.value)}
                    >
                        {ctx.settings.unitTypes.filter(p => p.hasUnits).map(item =>
                            <MenuItem key={item.unitTypeId} value={item.unitTypeId}>{item.name}</MenuItem>
                        )}
                    </Select>
                </FormControl>
                <UnitAutoCompleteHierarchical
                    label={t("User.BatchEdit.UnitLabel")}
                    disabled={unitTypeId == undefined}
                    selectedItems={unit ? [unit] : []}
                    multiSelect={false}
                    selectedMode={"free"}
                    unitTypeId={unitTypeId}
                    onChange={items => handleChangeUnit(items.length > 0 ? items[0] : undefined)}
                    onError={e => MprcRequestSnackbarErrorReport(e)}
                />
            </Stack>
        </Stack>
    );
};

export const UserRolesOperation: React.FC = () => {

    const { t } = useTranslation();
    const ctx = useUserBatchEditContext();

    const [props, setProps] = useState<UserOperationRoleProps>({ action: UserOperationAction.Add } as UserOperationRoleProps);
    const [role, setRole] = useState<AutoCompleteDataModel>();

    const handleUpdateRecord = (name: keyof UserOperationRoleProps, v: any) => {
        setProps(state => ({ ...state, [name]: v }));
    };

    const handleChangeRole = (item?: AutoCompleteDataModel) => {
        setRole(item);
        handleUpdateRecord("roleId", item?.key);
    };

    useEffect(() => {
        ctx.setOptions({ role: props });
    }, [props]);

    return (
        <Stack spacing={1}>
            <AddOrRemoveOperation value={props.action} onChange={value => handleUpdateRecord("action", value)} />
            <SecurityRoleAutoComplete
                label={t("User.BatchEdit.RoleLabel")}
                onChange={items => handleChangeRole(items.length > 0 ? items[0] : undefined)}
                selectedItems={role ? [role] : []}
            />
        </Stack>
    );
};

export const UserTopicsOperation: React.FC = () => {

    const [action, setAction] = useState<UserOperationAction>(UserOperationAction.Add);
    const [isRequired, setIsRequired] = useState<boolean>(true);
    const [topic, setTopic] = useState<AutoCompleteDataModel>();

    const { t } = useTranslation();
    const ctx = useUserBatchEditContext();

    useDidUpdateEffect(() => {
        ctx.setOptions({ topic: { action, topicId: topic?.key, isRequired } });
    }, [action, topic]);

    return (
        <Stack spacing={1}>

            <RadioGroup value={action} onChange={(e, value) => setAction(value as unknown as UserOperationAction)}>
                <Stack direction="row" spacing={20}>
                    <MprcRadio label={t("User.BatchEdit.AddTopicOption")} value={UserOperationAction.Add} />
                    <MprcSwitch
                        label={t("User.BatchEdit.RequiredTopicLabel")}
                        checked={isRequired}
                        disabled={action == UserOperationAction.Remove}
                        onChange={() => setIsRequired(!isRequired)}
                    />
                </Stack>
                <MprcRadio label={t("User.BatchEdit.RemoveTopicOption")} value={UserOperationAction.Remove} />
            </RadioGroup>


            <TopicAutoComplete
                label={t("User.BatchEdit.TopicLabel")}
                helperText={t("User.BatchEdit.TopicHint")}
                selectedItems={topic ? [topic] : []}
                onChange={items => setTopic(items.length > 0 ? items[0] : undefined)}
            />
        </Stack>
    );
};

export const UserGroupsOperation: React.FC = () => {

    const [action, setAction] = useState<UserOperationAction>(UserOperationAction.Add);
    const [group, setGroup] = useState<AutoCompleteDataModel>();

    const { t } = useTranslation();
    const ctx = useUserBatchEditContext();

    useDidUpdateEffect(() => {
        ctx.setOptions({ group: { action, groupId: group?.key } });
    }, [action, group]);

    return (
        <Stack spacing={1}>
            <AddOrRemoveOperation
                addText={t("User.BatchEdit.AddGroupOption")}
                removeText={t("User.BatchEdit.RemoveGroupOption")}
                onChange={value => setAction(value)}
                value={action}
            />
            <GroupAutoComplete
                label={t("User.BatchEdit.GroupLabel")}
                groupType={GroupType.Static}
                groupUsages={GroupUsages.Administration}
                helperText={t("User.BatchEdit.GroupHint")}
                selectedItems={group ? [group] : []}
                onChange={items => setGroup(items.length > 0 ? items[0] : undefined)}
            />
        </Stack>
    );
};

export const UserBlockOperation: React.FC = () => {

    const { t } = useTranslation();
    const ctx = useUserBatchEditContext();

    var [props, setProps] = useState<UserOperationBlockProps>({
        status: UserStatus.Active
    } as UserOperationBlockProps);

    const handleUpdateRecord = (name: keyof UserOperationBlockProps, v: any) => {
        setProps((state) => ({ ...state, [name]: v }));
    };

    useEffect(() => {
        ctx.setOptions({ block: props });
    }, [props]);

    return (
        <Stack spacing={1}>
            <FormControl fullWidth>
                <InputLabel>{t("User.BatchEdit.BlockLabel")}</InputLabel>
                <Select
                    value={props.status}
                    label={t("User.BatchEdit.BlockLabel")}
                    onChange={(e) => handleUpdateRecord("status", e.target.value)}
                >
                    <MenuItem value={UserStatus.Active}><Typography variant="h6" color="success.main" sx={{ textTransform: "uppercase" }}>{t("Enums.UserStatus.Active")}</Typography></MenuItem>
                    <MenuItem value={UserStatus.ActiveWithLoginBlocked}><Typography variant="h6" color="warning.main" sx={{ textTransform: "uppercase" }}>{t("Enums.UserStatus.ActiveWithLoginBlocked")}</Typography></MenuItem>
                    <MenuItem value={UserStatus.Inactive}><Typography variant="h6" color="error.main" sx={{ textTransform: "uppercase" }}>{t("Enums.UserStatus.Inactive")}</Typography></MenuItem>
                </Select>
            </FormControl>
            {
                props.status == UserStatus.Active &&
                <MprcDatePicker
                    disablePast
                    fullWidth
                    timePartHour="endDate"
                    label={t("User.Edit.AccessExpiresOnLabel")}
                    value={props.accessExpiresOn}
                    onChange={(date) => handleUpdateRecord("accessExpiresOn", date)}
                />
            }
            {props.status != UserStatus.Active &&
                <>
                    <FormControl fullWidth>
                        <InputLabel>{t("User.BatchEdit.BlockReasonLabel")}</InputLabel>
                        <Select
                            value={props.blockReasonId == null ? 0 : props.blockReasonId}
                            label={t("User.BatchEdit.BlockReasonLabel")}
                            onChange={(e) => handleUpdateRecord("blockReasonId", e.target.value == 0 ? null : e.target.value)}
                        >
                            {
                                ctx.settings.blockReasons.filter(p => p.appliesToUserStatus == props.status)
                                    .map((x) => (
                                        <MenuItem key={x.blockReasonId} value={x.blockReasonId}>
                                            {x.name}
                                        </MenuItem>
                                    ))
                            }
                        </Select>
                    </FormControl>
                    {
                        props.status == UserStatus.ActiveWithLoginBlocked &&
                        <MprcDatePicker
                            disablePast
                            fullWidth
                            timePartHour="endDate"
                            label={t("User.Edit.UnblockAtLabel")}
                            value={props.unblockAt}
                            onChange={(date) => handleUpdateRecord("unblockAt", date)}
                        />
                    }
                </>
            }
        </Stack>
    );
};

type GeneralInfoFields = "isExternalUser" | "preferredLocale" | "workplace" | "admissionDate" | "dismissedDate";

export const UserGeneralInfoOperation: React.FC = () => {

    const { t } = useTranslation();
    const session = useSession();
    const ctx = useUserBatchEditContext();

    const [field, setField] = useState<GeneralInfoFields>();
    const [workplace, setWorkplace] = useState<AutoCompleteDataModel>();
    const [props, setProps] = useState<UserOperationGeneralInfoProps>({} as UserOperationGeneralInfoProps);

    const handleUpdateRecord = (name: keyof UserOperationGeneralInfoProps, v: any) => {
        setProps({ [name]: v } as UserOperationGeneralInfoProps);
    };

    useEffect(() => {
        ctx.setOptions({ generalInfo: props });
    }, [props]);

    useEffect(() => {
        setProps((field == "preferredLocale" ? { preferredLocale: ctx.settings.locale } : {}) as UserOperationGeneralInfoProps);
    }, [field]);

    return (
        <Grid container spacing={4} alignItems="flex-end" direction="row">
            <Grid size="auto">
                <FormControl sx={{ width: 300 }}>
                    <InputLabel>{t("User.BatchEdit.SelectFieldLabel")}</InputLabel>
                    <Select
                        value={field || ""}
                        label={t("User.BatchEdit.SelectFieldLabel")}
                        onChange={(e) => setField(e.target.value as GeneralInfoFields)}
                    >
                        <MenuItem value="isExternalUser">{t("User.BatchEdit.ExternalUserOption")}</MenuItem>
                        <MenuItem value="preferredLocale">{t("User.BatchEdit.LocaleOption")}</MenuItem>
                        {ctx.settings.workplaceLabel.length > 0 && <MenuItem value="workplace">{ctx.settings.workplaceLabel}</MenuItem>}
                        <MenuItem value="admissionDate">{t("User.BatchEdit.AdmissionOption")}</MenuItem>
                        <MenuItem value="dismissedDate">{t("User.BatchEdit.DismissedOption")}</MenuItem>
                    </Select>

                </FormControl>
            </Grid>
            <Grid size="grow">
                {
                    field == "isExternalUser" ?
                        <MprcSwitch label="Sim" checked={props.extenalUser ?? false} onChange={(e, checked) => handleUpdateRecord("extenalUser", checked)} />
                        : field == "admissionDate"
                            ? <MprcDatePicker
                                label={t("User.Edit.AdmissionDateLabel")}
                                value={props.admissionDate}
                                onChange={(date) => handleUpdateRecord("admissionDate", date)}
                            />
                            :
                            field == "dismissedDate"
                                ? <MprcDatePicker
                                    label={t("User.Edit.DismissedDateLabel")}
                                    value={props.dismissedDate}
                                    onChange={(date) => handleUpdateRecord("dismissedDate", date)}
                                />
                                : field == "preferredLocale"
                                    ?
                                    <FormControl sx={{ width: 200 }}>
                                        <InputLabel>{t("User.Edit.LocaleLabel")}</InputLabel>
                                        <Select
                                            value={props.preferredLocale || ""}
                                            label={t("User.Edit.LocaleLabel")}
                                            onChange={(e) => handleUpdateRecord("preferredLocale", e.target.value)}
                                        >
                                            {session.config.inputLocales.map((l) => (
                                                <MenuItem key={l.locale} value={l.locale}>
                                                    {l.nameR.find(x => x.locale == session.uiCulture)?.text ?? l.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    :
                                    field == "workplace"
                                        ?
                                        ctx.settings.isWorkplaceFreeText === true
                                            ?
                                            <TextField
                                                fullWidth
                                                label={ctx.settings.workplaceLabel}
                                                margin="none"
                                                value={props.workplaceText || ""}
                                                onChange={(e) => handleUpdateRecord("workplaceText", e.target.value)}
                                                slotProps={{
                                                    input: {
                                                        inputProps: { maxLength: 255 }
                                                    }
                                                }}
                                            />
                                            :
                                            <WorkplaceAutoComplete
                                                label={ctx.settings.workplaceLabel}
                                                multiSelect={false}
                                                selectedItems={workplace != undefined ? [workplace] : []}
                                                onChange={(items) => {
                                                    handleUpdateRecord("workplaceId", items.length > 0 ? items[0].key : undefined);
                                                    setWorkplace(items.length > 0 ? items[0] : undefined);
                                                }}

                                            />
                                        : <></>


                }
            </Grid>
        </Grid>
    );
};


export const UserAdditionalInfoOperation: React.FC = () => {

    const { t } = useTranslation();
    const ctx = useUserBatchEditContext();

    const [fieldId, setFieldId] = useState<string>();
    const [props, setProps] = useState<UserOperationAdditionalInfoProps>({} as UserOperationAdditionalInfoProps);
    const inputRef = useRef<any>(undefined);

    const handleUpdateRecord = (name: keyof UserOperationAdditionalInfoProps, v: any) => {
        setProps({ [name]: v } as UserOperationAdditionalInfoProps);
    };

    const handleChangeField = (e: any) => {
        setFieldId(e.target.value);
        setProps({} as UserOperationAdditionalInfoProps);

        setTimeout(() => {
            inputRef.current?.focus();
        }, 50);
    };

    useEffect(() => {
        ctx.setOptions({ additionalInfo: props });
    }, [props]);

    const field = useMemo(() => ctx.settings.customFields.find(p => p.fieldId == fieldId), [ctx.settings.customFields, fieldId]);

    return (
        <Grid container spacing={4} alignItems="flex-end" direction="row">
            <Grid size="auto">
                <FormControl sx={{ width: 300 }}>
                    <InputLabel>{t("User.BatchEdit.SelectFieldLabel")}</InputLabel>
                    <Select
                        value={fieldId || ""}
                        displayEmpty
                        onChange={handleChangeField}
                    >
                        {ctx.settings.customFields.map(item =>
                            <MenuItem key={item.fieldId} value={item.fieldId}>{item.label}</MenuItem>
                        )}
                    </Select>
                </FormControl>
            </Grid>
            <Grid size="grow">
                {field &&
                    <CustomFieldInput
                        ref={inputRef}
                        margin="dense"
                        field={{ ...field, fieldValue: props?.customField?.fieldValue }}
                        onChange={(value, formattedValue, isValid) => {
                            handleUpdateRecord("customField", { ...field, fieldValue: value });
                        }}
                        validateOnFirstMount={true}
                    />
                }
            </Grid>
        </Grid>
    );
};


export const UserManagementOperation: React.FC = () => {

    const { t } = useTranslation();
    const ctx = useUserBatchEditContext();

    const MAIN_MANAGER_ID = "MAIN_MANAGER_ID";
    const MAIN_MANAGER_NAME = t("User.BatchEdit.MainManagerOption");

    const [managerTypeId, setManagerTypeId] = useState<string>(MAIN_MANAGER_ID);
    const [props, setProps] = useState<UserOperationManagementProps>({} as UserOperationManagementProps);
    const [manager, setManager] = useState<AutoCompleteDataModel>();

    const handleUpdateRecord = (name: keyof UserOperationManagementProps, v: any) => {
        setProps({ [name]: v } as UserOperationManagementProps);
    };

    const handleChangeManager = (user?: AutoCompleteDataModel) => {
        if (managerTypeId == MAIN_MANAGER_ID)
            handleUpdateRecord("managerId", user?.key);
        else
            handleUpdateRecord("secondaryManager", { managerTypeId, managerId: user?.key });

        setManager(user);
    };

    useEffect(() => {
        ctx.setOptions({ management: props });
    }, [props]);

    useEffect(() => {
        setProps({} as UserOperationManagementProps);
    }, [managerTypeId]);

    var managerTypeName = ctx.settings.secondaryManagers.find(p => p.managerTypeId == managerTypeId)?.managerTypeName ?? MAIN_MANAGER_NAME;

    return (
        <Stack spacing={1}>
            <FormControl sx={{ width: 300 }}>
                <InputLabel>{t("User.BatchEdit.ManagerTypeLabel")}</InputLabel>
                <Select
                    value={managerTypeId}
                    displayEmpty
                    onChange={(e) => setManagerTypeId(e.target.value)}
                >
                    <MenuItem key={MAIN_MANAGER_ID} value={MAIN_MANAGER_ID}>{MAIN_MANAGER_NAME}</MenuItem>
                    {ctx.settings.secondaryManagers.map(item =>
                        <MenuItem key={item.managerTypeId} value={item.managerTypeId}>{item.managerTypeName}</MenuItem>
                    )}
                </Select>
            </FormControl>
            <UserAutoComplete
                label={managerTypeName}
                onChange={(items) => handleChangeManager(items.length > 0 ? items[0] : undefined)}
                selectedItems={manager ? [manager] : []}
            />
        </Stack>
    );
};
